import React, { useState, useMemo, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import styled from "styled-components";
import Cards from "react-credit-cards-2";
import "react-credit-cards-2/dist/es/styles-compiled.css";

import { paymentSchema } from "../validators/payment";
import { formatTHB, formatCardNumber } from "../lib/formatters";
import PolicyModal from "../../../components/subscription/PolicyModal";

// react-credit-cards-2 renders at a fixed intrinsic size.
// We scale the whole card proportionally to fill its column.
const CARD_BASE_W = 290;
const CARD_BASE_H = 183; // ~ standard card aspect (1.586)
const CARD_MAX_SCALE = 1.9; // don't let it get absurdly large on wide screens

/**
 * Step 3 — Payment.
 * Order summary on top.
 * Desktop: card preview (left) + form fields (right) in a 50/50 split;
 *          the card scales up to fill its half.
 * Mobile (≤768px): card preview stacks on top of form.
 *
 * Discount calculation is client-side for display ONLY. Backend is the
 * source of truth and recomputes on submit.
 */
function PaymentStep({
  plan,
  promo,
  onSubmit,
  onBack,
  terms,
  privacy,
  refund,
  lang = "th",
}) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      card_number: "",
      card_holder: "",
      expiry_month: "",
      expiry_year: "",
      cvv: "",
      accept_terms: false,
    },
    mode: "onBlur",
  });

  // ─── Live card preview state ──────
  const [focused, setFocused] = useState("");

  const cardNumberRaw = watch("card_number");
  const cardNumberFormatted = formatCardNumber(cardNumberRaw);
  const cardHolder = watch("card_holder");
  const expiryMonth = watch("expiry_month");
  const expiryYear = watch("expiry_year");
  const cvv = watch("cvv");

  // react-credit-cards-2 wants expiry as one string "MM/YY"
  const expiryCombined = `${expiryMonth || ""}${
    expiryYear ? "/" + String(expiryYear).slice(-2) : ""
  }`;

  // ─── Responsive card scaling ──────
  // Measure the column width and scale the (fixed-size) card to fill it.
  const cardColRef = useRef(null);
  const [cardScale, setCardScale] = useState(1);

  useEffect(() => {
    const el = cardColRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width ?? CARD_BASE_W;
      // Scale at least 1 (never shrink below intrinsic), cap at MAX.
      const next = Math.min(Math.max(w / CARD_BASE_W, 1), CARD_MAX_SCALE);
      setCardScale(next);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const [policyOpen, setPolicyOpen] = useState(null);

  const { basePrice, discountAmount, finalAmount } = useMemo(() => {
    const base = Number(plan?.price ?? 0);
    let discount = 0;
    if (promo) {
      if (promo.discount_type === "PERCENT") {
        discount = (base * Number(promo.discount_value)) / 100;
      } else if (promo.discount_type === "FIXED") {
        discount = Math.min(base, Number(promo.discount_value));
      }
    }
    return {
      basePrice: base,
      discountAmount: discount,
      finalAmount: Math.max(0, base - discount),
    };
  }, [plan, promo]);

  const policyMap = {
    terms: {
      label: { th: "เงื่อนไขการใช้งาน", en: "Terms of Service" },
      data: terms,
    },
    privacy: {
      label: { th: "นโยบายความเป็นส่วนตัว", en: "Privacy Policy" },
      data: privacy,
    },
    refund: {
      label: { th: "นโยบายการคืนเงิน", en: "Refund Policy" },
      data: refund,
    },
  };

  return (
    <>
      <Form
        onSubmit={handleSubmit((data) => {
          console.log("🔍 PaymentStep submit data:", data);
          onSubmit(data);
        })}
        noValidate
      >
        <Header>
          <HeaderTextCol>
            <Title>{lang === "en" ? "Payment" : "ชำระเงิน"}</Title>
            <Subtitle>
              {lang === "en"
                ? "Secured by Omise — your card never touches our servers."
                : ""}
            </Subtitle>
          </HeaderTextCol>
          <BrandBadges aria-label="Accepted cards">
            <VisaBadge />
            <MastercardBadge />
            <JcbBadge />
            <AmexBadge />
          </BrandBadges>
        </Header>

        <Body>
          {/* Order summary */}
          <SummaryBox>
            <SummaryRow>
              <SummaryItem>
                {plan?.name_subscription_plan ?? "—"}
                {plan && (
                  <PeriodBadge>
                    {plan.installment_plan === "YEARLY"
                      ? lang === "en"
                        ? "Yearly"
                        : "รายปี"
                      : lang === "en"
                      ? "Monthly"
                      : "รายเดือน"}
                  </PeriodBadge>
                )}
              </SummaryItem>
              <SummaryValue>{formatTHB(basePrice)}</SummaryValue>
            </SummaryRow>

            {promo && discountAmount > 0 && (
              <SummaryRow $muted>
                <SummaryItem>
                  {promo.code}
                  {promo.discount_type === "PERCENT" && (
                    <span> (−{promo.discount_value}%)</span>
                  )}
                </SummaryItem>
                <SummaryValue>−{formatTHB(discountAmount)}</SummaryValue>
              </SummaryRow>
            )}

            <SummaryDivider />

            <SummaryRow>
              <SummaryTotal>
                {lang === "en" ? "Total to charge" : "ยอดที่จะตัด"}
              </SummaryTotal>
              <SummaryTotalValue>{formatTHB(finalAmount)}</SummaryTotalValue>
            </SummaryRow>
          </SummaryBox>

          {/* 50/50 layout: card preview | form fields */}
          <PaymentGrid>
            <CardPreviewCol ref={cardColRef}>
              <CardPreviewSticky>
                {/* Outer reserves the scaled footprint; inner is the
                    real (fixed-size) card transformed up. */}
                <CardScaleFrame
                  style={{
                    width: CARD_BASE_W * cardScale,
                    height: CARD_BASE_H * cardScale,
                  }}
                >
                  <CardScaleInner
                    style={{ transform: `scale(${cardScale})` }}
                  >
                    <Cards
                      number={cardNumberRaw || ""}
                      name={cardHolder || ""}
                      expiry={expiryCombined}
                      cvc={cvv || ""}
                      focused={focused}
                      placeholders={{
                        name: "CARDHOLDER NAME",
                      }}
                    />
                  </CardScaleInner>
                </CardScaleFrame>
              </CardPreviewSticky>
            </CardPreviewCol>

            <FormCol>
              <FieldGrid>
                <FieldCol $fullWidth>
                  <Label htmlFor="pay-num">
                    {lang === "en" ? "Card number" : "หมายเลขบัตร"}
                    <Required>*</Required>
                  </Label>
                  <Input
                    id="pay-num"
                    type="text"
                    inputMode="numeric"
                    autoComplete="cc-number"
                    placeholder="1234 5678 9012 3456"
                    value={cardNumberFormatted}
                    onFocus={() => setFocused("number")}
                    onChange={(e) =>
                      setValue(
                        "card_number",
                        e.target.value.replace(/\D/g, ""),
                        { shouldValidate: false }
                      )
                    }
                    onBlur={(e) =>
                      setValue(
                        "card_number",
                        e.target.value.replace(/\D/g, ""),
                        { shouldValidate: true }
                      )
                    }
                    $hasError={!!errors.card_number}
                    maxLength={23}
                  />
                  {errors.card_number && (
                    <ErrorText>{errors.card_number.message}</ErrorText>
                  )}
                </FieldCol>

                <SmallFieldCol>
                  <Label htmlFor="pay-mm">
                    {lang === "en" ? "Month" : "เดือน"}
                    <Required>*</Required>
                  </Label>
                  <Input
                    id="pay-mm"
                    type="text"
                    inputMode="numeric"
                    autoComplete="cc-exp-month"
                    placeholder="MM"
                    maxLength={2}
                    $hasError={!!errors.expiry_month}
                    {...register("expiry_month")}
                    onFocus={() => setFocused("expiry")}
                  />
                  {errors.expiry_month && (
                    <ErrorText>{errors.expiry_month.message}</ErrorText>
                  )}
                </SmallFieldCol>

                <SmallFieldCol>
                  <Label htmlFor="pay-yy">
                    {lang === "en" ? "Year" : "ปี"}
                    <Required>*</Required>
                  </Label>
                  <Input
                    id="pay-yy"
                    type="text"
                    inputMode="numeric"
                    autoComplete="cc-exp-year"
                    placeholder="YY"
                    maxLength={4}
                    $hasError={!!errors.expiry_year}
                    {...register("expiry_year")}
                    onFocus={() => setFocused("expiry")}
                  />
                  {errors.expiry_year && (
                    <ErrorText>{errors.expiry_year.message}</ErrorText>
                  )}
                </SmallFieldCol>

                <SmallFieldCol>
                  <Label htmlFor="pay-cvv">
                    CVV<Required>*</Required>
                  </Label>
                  <Input
                    id="pay-cvv"
                    type="text"
                    inputMode="numeric"
                    autoComplete="cc-csc"
                    placeholder="123"
                    maxLength={4}
                    $hasError={!!errors.cvv}
                    {...register("cvv")}
                    onFocus={() => setFocused("cvc")}
                  />
                  {errors.cvv && <ErrorText>{errors.cvv.message}</ErrorText>}
                </SmallFieldCol>

                <FieldCol $fullWidth>
                  <Label htmlFor="pay-name">
                    {lang === "en" ? "Name on card" : "ชื่อบนบัตร"}
                    <Required>*</Required>
                  </Label>
                  <Input
                    id="pay-name"
                    type="text"
                    autoComplete="cc-name"
                    placeholder="JOHN DOE"
                    $hasError={!!errors.card_holder}
                    $uppercase
                    {...register("card_holder")}
                    onFocus={() => setFocused("name")}
                  />
                  {errors.card_holder && (
                    <ErrorText>{errors.card_holder.message}</ErrorText>
                  )}
                </FieldCol>
              </FieldGrid>

              {/* Terms acceptance */}
              <TermsRow>
                <Checkbox
                  id="pay-terms"
                  type="checkbox"
                  {...register("accept_terms")}
                />
                <TermsLabel htmlFor="pay-terms">
                  {lang === "en" ? "I agree to the " : "ฉันยอมรับ"}
                  <PolicyLink
                    type="button"
                    onClick={() => setPolicyOpen("terms")}
                  >
                    {policyMap.terms.label[lang]}
                  </PolicyLink>
                  {", "}
                  <PolicyLink
                    type="button"
                    onClick={() => setPolicyOpen("privacy")}
                  >
                    {policyMap.privacy.label[lang]}
                  </PolicyLink>
                  {lang === "en" ? ", and " : ", และ"}
                  <PolicyLink
                    type="button"
                    onClick={() => setPolicyOpen("refund")}
                  >
                    {policyMap.refund.label[lang]}
                  </PolicyLink>
                </TermsLabel>
              </TermsRow>
              {errors.accept_terms && (
                <ErrorText style={{ marginTop: 4 }}>
                  {errors.accept_terms.message}
                </ErrorText>
              )}
            </FormCol>
          </PaymentGrid>
        </Body>

        <Footer>
          <SecondaryButton type="button" onClick={onBack}>
            {lang === "en" ? "← Back" : "← ย้อนกลับ"}
          </SecondaryButton>
          <PayButton type="submit" disabled={isSubmitting || finalAmount <= 0}>
            {lang === "en"
              ? `Pay ${formatTHB(finalAmount)}`
              : `ชำระ ${formatTHB(finalAmount)}`}
          </PayButton>
        </Footer>
      </Form>

      {policyOpen && (
        <PolicyModal
          show={true}
          onClose={() => setPolicyOpen(null)}
          title={policyMap[policyOpen].label[lang]}
          htmlSource={policyMap[policyOpen].data?.html}
          isLoading={policyMap[policyOpen].data?.isLoading}
          lang={lang}
        />
      )}
    </>
  );
}

export default PaymentStep;

// ─── Brand badge SVGs ──────────────────────────────────

const BADGE_W = 38;
const BADGE_H = 24;

function BadgeShell({ children, title, bg = "#ffffff" }) {
  return (
    <svg
      width={BADGE_W}
      height={BADGE_H}
      viewBox="0 0 38 24"
      role="img"
      aria-label={title}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{title}</title>
      <rect
        x="0.5"
        y="0.5"
        width="37"
        height="23"
        rx="3.5"
        fill={bg}
        stroke="#e5e7eb"
      />
      {children}
    </svg>
  );
}

function VisaBadge() {
  return (
    <BadgeShell title="Visa">
      <text
        x="19"
        y="16"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        fontSize="9"
        fontWeight="900"
        fontStyle="italic"
        fill="#1a1f71"
        letterSpacing="0.5"
      >
        VISA
      </text>
    </BadgeShell>
  );
}

function MastercardBadge() {
  return (
    <BadgeShell title="Mastercard">
      <circle cx="15" cy="12" r="6" fill="#eb001b" />
      <circle cx="23" cy="12" r="6" fill="#f79e1b" opacity="0.9" />
      <path d="M19 7.2a6 6 0 0 0 0 9.6 6 6 0 0 0 0-9.6z" fill="#ff5f00" />
    </BadgeShell>
  );
}

function JcbBadge() {
  return (
    <BadgeShell title="JCB">
      <rect x="3" y="4" width="32" height="16" rx="2" fill="#ffffff" />
      <rect x="3" y="4" width="10.67" height="16" fill="#0e4c96" />
      <rect x="13.67" y="4" width="10.67" height="16" fill="#bf2030" />
      <rect x="24.33" y="4" width="10.67" height="16" fill="#007b3a" />
      <text
        x="19"
        y="15"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        fontSize="7"
        fontWeight="900"
        fill="#ffffff"
      >
        JCB
      </text>
    </BadgeShell>
  );
}

function AmexBadge() {
  return (
    <BadgeShell title="American Express" bg="#006fcf">
      <text
        x="19"
        y="11"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        fontSize="4.5"
        fontWeight="700"
        fill="#ffffff"
        letterSpacing="0.3"
      >
        AMERICAN
      </text>
      <text
        x="19"
        y="17"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        fontSize="4.5"
        fontWeight="700"
        fill="#ffffff"
        letterSpacing="0.3"
      >
        EXPRESS
      </text>
    </BadgeShell>
  );
}

// ─── Styled ──────────────────────────────────

const Form = styled.form`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Header = styled.div`
  padding: 24px 28px 12px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const HeaderTextCol = styled.div`
  flex: 1;
  min-width: 0;
`;

const BrandBadges = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
  align-items: center;
`;

const Title = styled.h5`
  && {
    font-weight: 700;
    font-size: 18px;
    color: #111827;
    margin: 0 0 4px 0;
  }
`;

const Subtitle = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const Body = styled.div`
  padding: 12px 28px 16px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }
`;

// ─── Summary ──────

const SummaryBox = styled.div`
  background: #f9fafb;
  border: 1px solid #f1f3f5;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 24px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 13px;
  color: ${({ $muted }) => ($muted ? "#6b7280" : "#374151")};

  & + & {
    margin-top: 6px;
  }
`;

const SummaryItem = styled.span`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const PeriodBadge = styled.span`
  font-size: 11px;
  color: #9ca3af;
`;

const SummaryValue = styled.span`
  font-size: 13px;
  font-weight: 500;
`;

const SummaryDivider = styled.hr`
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 10px 0 8px;
`;

const SummaryTotal = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: #111827;
`;

const SummaryTotalValue = styled.span`
  font-weight: 700;
  font-size: 18px;
  color: #111827;
`;

// ─── 50/50 payment layout ──────

const PaymentGrid = styled.div`
  display: grid;
  /* 50/50 split. minmax(0, 1fr) on both prevents the form column
     from pushing the grid wider than its container. */
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 28px;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 20px;
  }
`;

const CardPreviewCol = styled.div`
  display: flex;
  justify-content: center;
  min-width: 0;
`;

const CardPreviewSticky = styled.div`
  position: sticky;
  top: 0;

  .rccs {
    margin: 0;
  }

  @media (max-width: 768px) {
    position: static;
  }
`;

/* Reserves the *scaled* footprint in normal flow so nothing overlaps. */
const CardScaleFrame = styled.div`
  position: relative;
`;

/* The real card sits at its intrinsic size, scaled from the top-left
   so it exactly fills the frame above. */
const CardScaleInner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: top left;
`;

const FormCol = styled.div`
  min-width: 0; /* prevent grid blowout from long content */
`;

// ─── Form fields ──────

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FieldCol = styled.div`
  ${({ $fullWidth }) => $fullWidth && "grid-column: 1 / -1;"}
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const SmallFieldCol = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Label = styled.label`
  && {
    font-size: 12px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 4px;
    display: block;
  }
`;

const Required = styled.span`
  color: #ef4444;
  margin-left: 4px;
`;

const Input = styled.input`
  width: 100%;
  height: 40px;
  font-size: 14px;
  font-weight: 400;
  color: #111827;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 12px;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  font-family: inherit;
  ${({ $uppercase }) => $uppercase && "text-transform: uppercase;"}

  &::placeholder {
    color: #9ca3af;
    text-transform: none;
  }

  &:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }

  ${({ $hasError }) =>
    $hasError &&
    `
    border-color: #ef4444;
    &:focus {
      border-color: #ef4444;
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
    }
  `}
`;

const ErrorText = styled.div`
  color: #ef4444;
  font-size: 11px;
  margin-top: 4px;
`;

// ─── Terms ──────

const TermsRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 24px;
`;

const Checkbox = styled.input`
  margin-top: 3px;
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #6366f1;
  flex-shrink: 0;
`;

const TermsLabel = styled.label`
  && {
    font-size: 13px;
    color: #374151;
    cursor: pointer;
    line-height: 1.5;
  }
`;

const PolicyLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  color: #6366f1;
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;
  font-family: inherit;

  &:hover {
    color: #4f46e5;
  }
`;

// ─── Footer ──────

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 28px;
  border-top: 1px solid #f1f3f5;
  background: #ffffff;
`;

const buttonBase = `
  height: 40px;
  padding: 0 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s, border-color 0.15s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PayButton = styled.button`
  && {
    ${buttonBase}
    min-width: 200px;
    background: #6366f1;
    color: #ffffff;

    &:hover:not(:disabled) {
      background: #4f46e5;
    }
  }
`;

const SecondaryButton = styled.button`
  && {
    ${buttonBase}
    background: #ffffff;
    color: #6b7280;
    border: 1px solid #d1d5db;

    &:hover:not(:disabled) {
      border-color: #9ca3af;
      color: #374151;
    }
  }
`;